import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./CreateProfile.css";
import {
  AppRoutes,
  Box,
  Loader,
  LOCAL_STORAGE_KEYS,
  ProfileCreationTracker,
  Text,
  TextVariants,
} from "reusable";
import { useUser, useLocalStorage, useCreator } from "../../backend";
import CreatorQuestions from "./CreatorQuestions";
import { serverTimestamp } from "firebase/firestore";
import { CompleteProfile } from "models";
import { CreatorProfile } from "models";

const CreateProfile = (): JSX.Element => {
  //imports
  const goTo = useNavigate();
  const { stepInProgress, previewCreatorProfile } = useLocalStorage();
  const {
    creatorAnswers,
    setCreatorAnswers,
    getProfile,
    creatorProfile,
    getSummary,
  } = useCreator();

  //states
  const [step, setStep] = useState<number>(stepInProgress);
  const { loggedInUser, publishProfile, isProfileCreationInProgress } =
    useUser();
  
  // Ref to track if we've initialized from preview
  const hasInitializedFromPreview = useRef(false);

  // functions
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.STEP_IN_PROGRESS,
        String(step - 1)
      );
    } else {
      goTo(-1);
    }
  };

  const handleNextStep = async (difference: number) => {
    if (step + difference < 4) {
      setStep(step + difference);
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.STEP_IN_PROGRESS,
        String(step + difference)
      );
    } else {
      const publishCallback = (userId: string) => {
        goTo(AppRoutes.PROFILE.replace(":userId", userId));
      };
      // Debug log to check creatorAnswers before publishing profile
      // Sanitize creatorAnswers to remove undefined/null fields, but keep creationTime and location
      const sanitizedAnswers = {
        ...creatorAnswers,
        userId: loggedInUser?.userId || "",
        jobLocation: creatorAnswers.jobLocation || creatorAnswers.location || null,
        creatorPhotos: Array.isArray(creatorAnswers.creatorPhotos)
          ? creatorAnswers.creatorPhotos.filter((x: string) => !!x && typeof x === 'string' && !x.startsWith("blob:"))
          : [],
        profilePicture: Array.isArray(creatorAnswers.profilePicture)
          ? creatorAnswers.profilePicture[0]
          : creatorAnswers.profilePicture,
      };
      // Remove undefined/null fields but DO NOT remove creationTime or location
      Object.keys(sanitizedAnswers as any).forEach(key => {
        if ((sanitizedAnswers as any)[key] === undefined || (sanitizedAnswers as any)[key] === null) {
          delete (sanitizedAnswers as any)[key];
        }
      });
      // Ensure location.lat and location.lng are strings if location exists
      if (sanitizedAnswers.location) {
        sanitizedAnswers.location = {
          ...sanitizedAnswers.location,
          lat: String(sanitizedAnswers.location.lat),
          lng: String(sanitizedAnswers.location.lng)
        };
      }
      // Ensure creationTime is a Firestore Timestamp
      if (sanitizedAnswers.creationTime && typeof sanitizedAnswers.creationTime !== 'object') {
        try {
          const { Timestamp } = await import('firebase/firestore');
          sanitizedAnswers.creationTime = Timestamp.fromDate(new Date(sanitizedAnswers.creationTime));
        } catch (e) {
          // fallback: leave as is
        }
      }
      // Remove undefined/null fields but DO NOT remove creationTime or location
      Object.keys(sanitizedAnswers as any).forEach(key => {
        if ((sanitizedAnswers as any)[key] === undefined || (sanitizedAnswers as any)[key] === null) {
          delete (sanitizedAnswers as any)[key];
        }
      });
      // Ensure location.lat and location.lng are strings if location exists
      if (sanitizedAnswers.location) {
        sanitizedAnswers.location = {
          ...sanitizedAnswers.location,
          lat: String(sanitizedAnswers.location.lat),
          lng: String(sanitizedAnswers.location.lng)
        };
      }
      console.log('[CreateProfile] Full payload to Firestore:', sanitizedAnswers);
      await publishProfile(
        sanitizedAnswers as CreatorProfile,
        publishCallback,
        Boolean(loggedInUser?.isCreatorQuestionsAnswered)
      );
      // --- JOB POSTING LOGIC: Call Cloud Function directly after profile update ---
      if (
        creatorAnswers.position === 'Business Owner' &&
        creatorAnswers.isHiring &&
        creatorAnswers.jobPosition &&
        creatorAnswers.requiredExperience &&
        creatorAnswers.requiredEducation &&
        creatorAnswers.jobSalary &&
        creatorAnswers.requiredSoftwareSkills &&
        creatorAnswers.jobType &&
        (creatorAnswers.jobLocation || creatorAnswers.location) &&
        creatorAnswers.jobDescription
      ) {
        try {
          const jobLocation = creatorAnswers.jobLocation || creatorAnswers.location;
          console.log('[CreateProfile] Calling saveJobPosting with:', {
            userId: creatorAnswers.userId,
            jobPosition: creatorAnswers.jobPosition,
            requiredExperience: creatorAnswers.requiredExperience,
            requiredEducation: creatorAnswers.requiredEducation,
            jobSalary: creatorAnswers.jobSalary,
            requiredSoftwareSkills: creatorAnswers.requiredSoftwareSkills,
            jobType: creatorAnswers.jobType,
            jobLocation,
            jobDescription: creatorAnswers.jobDescription
          });
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const functions = getFunctions();
          const saveJobPosting = httpsCallable(functions, 'saveJobPosting');
          await saveJobPosting({
            userId: creatorAnswers.userId,
            jobPosition: creatorAnswers.jobPosition,
            requiredExperience: creatorAnswers.requiredExperience,
            requiredEducation: creatorAnswers.requiredEducation,
            jobSalary: creatorAnswers.jobSalary,
            requiredSoftwareSkills: creatorAnswers.requiredSoftwareSkills,
            jobType: creatorAnswers.jobType,
            jobLocation,
            jobDescription: creatorAnswers.jobDescription
          });
        } catch (error) {
          console.error('Error posting job:', error);
        }
      }
      // --- END JOB POSTING LOGIC ---
    }
  };

  const handleOnClickStep = (updatedStep: number) => {
    if (stepInProgress > updatedStep) {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.STEP_IN_PROGRESS,
        String(updatedStep)
      );
      setStep(updatedStep);
    }
  };

  const setAnswers = (answers: CompleteProfile) => {
    setCreatorAnswers(answers);
  };

  // effects
  useEffect(() => {
    if (
      loggedInUser?.userId?.length &&
      loggedInUser?.isCreatorQuestionsAnswered
    ) {
      getProfile(loggedInUser?.userId);
      getSummary(loggedInUser?.userId);
    }
  }, [loggedInUser?.userId, loggedInUser?.isCreatorQuestionsAnswered, getProfile, getSummary]);

  // Initialize profile data only once when user loads
  useEffect(() => {
    if (loggedInUser?.userId && creatorProfile?.userId === loggedInUser?.userId) {
      setCreatorAnswers(prev => ({
        ...prev,
        ...creatorProfile,
        profilePicture: Array.isArray(creatorProfile.profilePicture)
          ? creatorProfile.profilePicture.filter((x): x is string => typeof x === 'string' && !!x)
          : [creatorProfile.profilePicture].filter((x): x is string => typeof x === 'string' && !!x),
        creatorPhotos: Array.isArray(creatorProfile.creatorPhotos)
          ? creatorProfile.creatorPhotos.filter((x): x is string => !!x && !x.startsWith("blob:"))
          : [],
        // Clear any previous edit state to prevent duplicates
        newCreatorImages: [],
        newProfilePicture: [],
        removedUrls: [],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser?.userId, creatorProfile?.userId]);

  // Initialize from preview if available - only once
  useEffect(() => {
    if (!hasInitializedFromPreview.current && Object.keys(previewCreatorProfile)?.length && !creatorProfile?.userId) {
      hasInitializedFromPreview.current = true;
      setCreatorAnswers(prev => ({
        ...previewCreatorProfile,
        profilePicture: prev?.profilePicture || previewCreatorProfile.profilePicture,
        creatorPhotos: prev?.creatorPhotos || previewCreatorProfile.creatorPhotos,
        removedUrls: prev?.removedUrls || [],
        newCreatorImages: prev?.newCreatorImages || [],
        newProfilePicture: prev?.newProfilePicture || undefined,
      }));
    }
    // Only run when creatorProfile changes - not previewCreatorProfile
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorProfile?.userId]);

  return (
    <>
      {!isProfileCreationInProgress ? (
        <Box className="create-profile-container">
          <Box className="create-profile-tracker">
            <Box
              className="header-back-button hide-for-small-only"
              onClick={handleBack}
              position="absolute"
              left="113px"
            ></Box>
            <ProfileCreationTracker
              step={step}
              handleOnClickStep={handleOnClickStep}
            />
          </Box>
          <Box className="app-container creator-questions-container">
            <CreatorQuestions
              step={step}
              setStep={(difference) => handleNextStep(difference)}
              creatorAnswers={creatorAnswers}
              setCreatorAnswers={setAnswers}
            />
          </Box>
        </Box>
      ) : (
        <>
          <Box className="loader-container modal-overlay">
            <Box textAlign="center">
              <Text
                variant={TextVariants.MEDIUM_15_TERNARY_FONT_BOLD}
                style={{ lineHeight: "20px" }}
              >
                Please wait while we{" "}
                {loggedInUser?.isCreatorQuestionsAnswered
                  ? "Update "
                  : "Create "}{" "}
                your profile !
              </Text>
              <Box marginTop="20px">
                <Loader />
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default CreateProfile;